import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { useMemo, useState } from "react";

import {
  hasCptSeparator,
  parseCptCodeList,
  splitCompleteCptCodes,
} from "../helpers";

const VIRTUAL_SCROLL_THRESHOLD = 100;

export interface ICptCodeMultiSelectProps {
  /** Every CPT code with a row for the current account + provider. */
  options: string[];
  value: string[];
  onChange: (codes: string[]) => void;
  isLoading?: boolean;
  className?: string;
  /**
   * Called when a pasted list contained codes that aren't in `options` — i.e.
   * codes with no rates here, which therefore can't be selected. Lets the
   * caller surface them instead of dropping them silently.
   */
  onUnmatchedCodes?: (result: {
    unmatched: string[];
    matchedCount: number;
    totalCount: number;
  }) => void;
}

/**
 * CPT code picker for the All Payer Rates filter. On top of the usual
 * type-to-search, its search box accepts a whole list of codes at once —
 * pasted from a spreadsheet, a contract, or an email — and checks every code
 * it recognizes, so the table narrows to exactly that list in one action.
 */
export function CptCodeMultiSelect({
  options,
  value,
  onChange,
  isLoading = false,
  className,
  onUnmatchedCodes,
}: ICptCodeMultiSelectProps) {
  // Codes floated to the top of the dropdown, in the order they were chosen.
  // Without this a pasted list is checked but scattered through hundreds of
  // alphabetical options, so the panel opens on unchecked rows and looks like
  // nothing happened. It's a snapshot rather than a live read of `value`, so
  // ticking a box by hand doesn't make the row jump out from under the cursor;
  // the order is re-taken each time the panel opens.
  const [pinnedCodes, setPinnedCodes] = useState<string[]>([]);

  // Memoized so MultiSelect doesn't re-diff a new array on every parent render
  // (the CPT list can run into the hundreds).
  const selectOptions = useMemo(() => {
    const all = options.map((c) => ({ label: c, value: c }));
    const pinnedOrder = new Map(pinnedCodes.map((c, i) => [c, i]));
    if (pinnedOrder.size === 0) return all;
    const rank = (code: string) => pinnedOrder.get(code) ?? 0;
    return [
      ...all
        .filter((o) => pinnedOrder.has(o.value))
        .sort((a, b) => rank(a.value) - rank(b.value)),
      ...all.filter((o) => !pinnedOrder.has(o.value)),
    ];
  }, [options, pinnedCodes]);

  // Normalized (trimmed, upper-cased) lookup so a pasted list matches
  // regardless of casing or padding, while the selection keeps the option's own
  // spelling — the value the API filters on.
  const optionByNormalized = useMemo(() => {
    const map = new Map<string, string>();
    options.forEach((code) => map.set(code.trim().toUpperCase(), code));
    return map;
  }, [options]);

  const handleChange = (codes: string[]) => {
    onChange(codes);
    // Clearing the selection drops the pinning too, so the list goes back to
    // plain alphabetical rather than keeping now-unchecked codes on top.
    if (codes.length === 0) setPinnedCodes([]);
  };

  /** Checks every recognized code in a pasted or typed list. */
  const applyCodeList = (raw: string) => {
    const tokens = parseCptCodeList(raw);
    if (tokens.length === 0) return;

    const matched: string[] = [];
    const unmatched: string[] = [];
    tokens.forEach((token) => {
      const option = optionByNormalized.get(token);
      if (option) matched.push(option);
      else unmatched.push(token);
    });

    if (matched.length > 0) {
      // Pasted codes go to the front so the user sees the list they pasted,
      // checked, without scrolling for it.
      const merged = Array.from(new Set([...matched, ...value]));
      onChange(merged);
      setPinnedCodes(merged);
    }

    if (unmatched.length > 0) {
      onUnmatchedCodes?.({
        unmatched,
        matchedCount: matched.length,
        totalCount: tokens.length,
      });
    }
  };

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    filterOptions: any
  ) => {
    const { complete, partial } = splitCompleteCptCodes(event.target.value);
    if (!complete) {
      // Single, still-unterminated code — plain search-as-you-type.
      filterOptions.filterOptions.filter(event);
      return;
    }
    applyCodeList(complete);
    filterOptions.filterOptions.filter({ target: { value: partial } });
  };

  const handleFilterPaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    filterOptions: any
  ) => {
    const pasted = event.clipboardData?.getData("text") ?? "";
    // A single code pastes normally and just narrows the list.
    if (!hasCptSeparator(pasted)) return;
    event.preventDefault();
    // Assumes the caret is at the end, which it is for the common
    // click-in-and-paste flow; anything already typed counts as the first code.
    applyCodeList(`${filterOptions.props?.filterValue ?? ""} ${pasted}`);
    // Clear the box through `filter` rather than `filterOptions.reset` —
    // PrimeReact v8 never hands its panel header a `resetFilter`, so calling
    // reset() throws.
    filterOptions.filterOptions.filter({ target: { value: "" } });
  };

  const filterTemplate = (filterOptions: any) => (
    <div className={filterOptions.className}>
      <InputText
        // Carries PrimeReact's own filter ref so the box still auto-focuses
        // when the panel opens.
        ref={filterOptions.props?.filterRef}
        className="p-multiselect-filter"
        value={filterOptions.props?.filterValue ?? ""}
        onChange={(e) => handleFilterChange(e, filterOptions)}
        onPaste={(e) => handleFilterPaste(e, filterOptions)}
        placeholder="Search or paste codes (99213, 70450…)"
        aria-label="Search or paste CPT codes"
        autoComplete="off"
      />
      <span className="p-multiselect-filter-icon pi pi-search" />
    </div>
  );

  return (
    <MultiSelect
      value={value}
      options={selectOptions}
      onChange={(e) => handleChange(e.value ?? [])}
      placeholder={isLoading ? "Loading…" : "CPT Codes"}
      filter
      filterTemplate={filterTemplate}
      // Re-snapshot the pin order on open so codes chosen while the panel was
      // last open are grouped back up top.
      onShow={() => setPinnedCodes(value)}
      showClear
      display="comma"
      maxSelectedLabels={2}
      selectedItemsLabel="{0} CPT codes selected"
      className={className}
      disabled={isLoading}
      emptyFilterMessage="No CPT codes match"
      // Only worth virtualizing the long lists; below the threshold the plain
      // list is cheap and behaves better (and renders under jsdom, which gives
      // the virtual scroller no viewport to measure).
      virtualScrollerOptions={
        options.length > VIRTUAL_SCROLL_THRESHOLD ? { itemSize: 38 } : undefined
      }
      tooltip="Search, or paste a comma-separated list of CPT codes to filter to just those codes"
      tooltipOptions={{ position: "top", showDelay: 400 }}
    />
  );
}
